import { metadata, getRouter } from 'lib_ui-services';
import isSerializedEachImport from './_serializedEach/isSerializedEachImport';
import resolveItemReportFilterTarget from './_serializedEach/resolveItemReportFilterTarget';
import buildImportSavedFilter from './_serializedEach/buildImportSavedFilter';

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 */

const _p = {
    metadata,
    getRouter,
    isSerializedEachImport,
    resolveItemReportFilterTarget,
    buildImportSavedFilter
};
export const _private = _p;

/**
 * Serialized Each — create a personal saved filter when an auto-serialization import completes (#854,
 * import-saved-filter WORKING-DOC).
 *
 * The saved filter is fundamentally a client construct (routePath + search-element-keyed `filters`),
 * so it is built here — where the report route and element id are resolvable — rather than server-side.
 * Runs alongside the generic `didCreate_import_import` (variant-suffixed rules coexist, each self-gating);
 * this one no-ops for every non-Serialized-Each import.
 */
export default {
    verb: 'didCreate',
    namespace: 'import',
    relation: 'import',
    description: 'Create a personal saved filter for a Serialized Each (auto-serialization) import.',
    logic: createSerializedEachSavedFilter
};

/**
 * @param {{
 *   data: { newRecord?: object, result?: { title?: string, totalRecords?: number, failed?: any[] } };
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>;
 *   log: LoggingProvider;
 * }} parameters
 */
async function createSerializedEachSavedFilter({ data, context, dispatch, log }) {
    const { newRecord, result } = data || {};

    // Only auto-serialization (model+quantity) imports (§3a — mirrors the server autoSerialization gate).
    if (!_p.isSerializedEachImport(newRecord)) return;

    // Nothing to pin to if nothing was imported. Prefer the server-authoritative, fan-out-aware
    // success count: `totalRecords` here is the CLIENT source-row count, while `failed` is the SERVER
    // post-fan-out record-failure list, so `totalRecords - failed.length` mixes units and can go
    // negative when a model+quantity row expands to many records and most fail (e.g. 3 rows → 10
    // records, 9 failed, 1 succeeded), wrongly suppressing the filter even though a record imported.
    // `importedRecords` accounts for fan-out; `recordsCreated` is the server import record's
    // totalRecords (successes AND failures, same units as `failed`), so subtract the failures when
    // falling back to it. Fall back to the row math only when the server reported no counts at all
    // (non-fan-out import paths leave them null).
    const importedCount =
        typeof result?.importedRecords === 'number'
            ? result.importedRecords
            : typeof result?.recordsCreated === 'number'
            ? result.recordsCreated - (result?.failed?.length || 0)
            : (result?.totalRecords || 0) - (result?.failed?.length || 0);
    if (importedCount <= 0) return;

    const importName = result?.title || newRecord?.title;
    if (!importName) return;

    // Resolve the report route + hidden Import element id from metadata (§5a/§2). Returns null until the
    // hidden `meta.import` search element is authored into the report page → this rule then no-ops safely.
    const target = _p.resolveItemReportFilterTarget({ metadata: _p.metadata, getRouter: _p.getRouter });
    if (!target) {
        log?.info?.(
            `[SerializedEach] No item-report Import filter element in metadata; skipping saved filter for import '${importName}'.`
        );
        return;
    }

    const record = _p.buildImportSavedFilter({
        importName,
        briefUserReference: context?.session?.briefUserReference,
        routePath: target.routePath,
        importElementId: target.importElementId
    });

    try {
        // `awaitResult: true` REQUESTS the create workflow and resolves on its completion, rather than
        // fire-and-forget publishing. Without it the promise resolves as soon as the event is dispatched,
        // so the workflow's own failures (e.g. duplicate title on re-import) would escape this catch and
        // could surface to the user after the import-success UI — exactly what "best-effort" must prevent.
        // Awaiting also makes the success log below truthful (the filter really was created, not just queued).
        await dispatch(
            { newRecord: record },
            { verb: 'create', namespace: 'application', relation: 'savedFilter' },
            { awaitResult: true }
        );
        log?.info?.(`[SerializedEach] Created personal saved filter '${record.title}' at ${target.routePath}.`);
    } catch (err) {
        // Best-effort: a failure (e.g. duplicate title on re-import) must not surface after the import success UI.
        log?.warn?.(`[SerializedEach] Failed to create saved filter '${record.title}'`, err);
    }
}
